// SCR-311: one Assistant conversation. The transcript (operator and assistant text, one collapsed
// line per tool read), the right rail (installation, turns, spend when the API sends it, End) and
// the composer. The transcript is polled with `?after=<seq>` every 1.5 s while a turn runs (no
// event stream); a paused session (`awaiting_operator`) asks for a message, an ended one is
// read-only with why it ended. Refusals are explained with their Help topic. The session's
// proposed changes (API-312) are cards under the transcript, read again whenever a change event
// arrives; deciding one polls once more for the Assistant's answer. No id is shown (AS-1).
//
// A transport whose turns stream (the proxy's API-586, `transport.turn`) is not polled while its
// turn runs: each event is appended as it arrives (a change event is its card at once), and when
// the turn ends, done or failed, the rows after the last one held before it are read again, so
// the transcript shown is the API's; a session read `active` with no stream running is idle (a
// turn that died without its closing step). The proxy never shows spend, and names a session
// AllCode's staff started "AllCode support".
//
// A session the operator's Claude Code drives (`channel = mcp`, M3) is labelled so and has no
// composer: the API refuses a browser's message to it. Its change cards are decided here as on
// any session; Claude Code's approval links open this page.
import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { ApiError } from "../../api/errors";
import { useHost } from "../../host/host";
import { HelpButton } from "../../primitives/help";
import { Link } from "../../primitives/link";
import { Chip, PageHeader } from "../../primitives/page";
import { Loading } from "../../primitives/states";
import { Money } from "../../primitives/table";
import { useScopeMe } from "../../scope/scope";
import { ChangeCard } from "./change-card";
import { effectiveStatus, mergeChanges, type AgentChange } from "./confirmations";
import { AssistantError } from "./refusal";
import {
  type AgentMessageList,
  applyPoll,
  applySent,
  changesToFetch,
  CLAUDE_CODE_HELP,
  drivenFromClaudeCode,
  emptyTranscript,
  endReasonText,
  idleWithoutStream,
  isEnded,
  isThinking,
  MAX_MESSAGE_LENGTH,
  POLL_INTERVAL_MS,
  shouldPoll,
  startersFor,
  STATE_LABELS,
  STATE_TONES,
  transcriptRows,
  when,
  type AgentMessage,
  type AgentSession,
  type Transcript,
  type TranscriptRow,
} from "./model";
import { ChannelChip, continuesHere, onProxy, startedBy } from "./sessions";
import { turnRefusalFrom, useAssistantTransport, type TurnEvent } from "./transport";

/** A row streamed before the API's history is read again; the re-read replaces every row after the turn's start. */
function streamedRow(seq: number, kind: AgentMessage["kind"], body: Record<string, unknown>, toolName: string | null = null, outcome: AgentMessage["outcome"] = null): AgentMessage {
  return { seq, kind, toolName, body, outcome, costMicroUsd: null, createdAt: new Date().toISOString() };
}

/** The transcript row an event appends, or null for the last event (it carries no row). */
export function eventRow(event: TurnEvent): AgentMessage | null {
  switch (event.event) {
    case "message":
      return streamedRow(event.seq, "assistant", { text: event.text });
    case "tool":
      return streamedRow(event.seq, "tool_result", {}, event.name, event.outcome);
    case "change":
      return streamedRow(event.seq, "change_event", {});
    default:
      return null;
  }
}

/** A streamed row's key: a change event carries its write's `tool_result` seq, so seq alone would drop the tool row (N1). */
const rowKey = (m: AgentMessage) => `${m.seq}:${m.kind}`;

/** The transcript with `rows` kept once by `(seq, kind)`, in seq order. */
export function withRows(transcript: Transcript, rows: readonly AgentMessage[]): Transcript {
  const byKey = new Map(transcript.messages.map((m) => [rowKey(m), m]));
  for (const row of rows) byKey.set(rowKey(row), row);
  const messages = [...byKey.values()].sort((a, b) => a.seq - b.seq);
  return { ...transcript, messages, lastSeq: Math.max(transcript.lastSeq, ...rows.map((r) => Math.floor(r.seq))) };
}

/** The transcript as it stood at `before`, with the API's rows after it (the end of a streamed turn). */
export function resynced(transcript: Transcript, before: number, list: AgentMessageList): Transcript {
  const kept = { ...transcript, messages: transcript.messages.filter((m) => m.seq <= before), lastSeq: before, awaitingReplyTo: null };
  return applyPoll(kept, list);
}

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export interface AssistantConversationProps {
  sessionId: string;
  /** The sessions list in the mounting shell (the back link). */
  sessionsHref: string;
  /** A starter request to put in the composer (SCR-310's starter, via the URL). */
  initialText?: string | undefined;
  /** The poll interval, for a test. */
  pollMs?: number | undefined;
}

export function AssistantConversation({ sessionId, sessionsHref, initialText = "", pollMs = POLL_INTERVAL_MS }: AssistantConversationProps) {
  const transport = useAssistantTransport();
  const host = useHost();
  /** A streamed turn is running: no poll meanwhile, its events are the transcript's news. */
  const [streaming, setStreaming] = useState(false);
  /** The last streamed turn stopped at its tool limit. */
  const [toolLimit, setToolLimit] = useState(false);
  const [session, setSession] = useState<AgentSession | null>(null);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let current = true;
    setSession(null);
    setTranscript(null);
    setLoadError(null);
    Promise.all([transport.get(sessionId), transport.poll(sessionId, 0)])
      .then(([loaded, list]) => {
        if (!current) return;
        setSession(loaded);
        setTranscript(applyPoll(emptyTranscript(loaded.state), list));
      })
      .catch((err: unknown) => current && setLoadError(unexpected(err)));
    return () => {
      current = false;
    };
  }, [transport, sessionId, attempt]);

  // One poll at a time, `after` the last row held, while a turn runs or after a send. Each answered
  // poll bumps `tick`, so the next is scheduled even when it brought no new row.
  const [tick, setTick] = useState(0);
  const held = useRef(transcript);
  held.current = transcript;
  // On a transport whose turns stream, a turn runs only inside its stream: `active` without one
  // is idle (B2), so it is neither polled nor shown as working.
  const view = transcript !== null && transport.turn ? idleWithoutStream(transcript) : transcript;
  const polling = !streaming && view !== null && shouldPoll(view);
  useEffect(() => {
    if (!polling || !held.current) return;
    let current = true;
    const { lastSeq, hasMore } = held.current;
    const timer = setTimeout(
      () => {
        transport
          .poll(sessionId, lastSeq)
          .then((list) => {
            if (!current) return;
            setTranscript((t) => (t ? applyPoll(t, list) : t));
            setActionError(null);
          })
          .catch((err: unknown) => {
            if (!current) return;
            setActionError(unexpected(err));
            // A refused poll stops the loop until the operator acts; the transcript stays as held.
            setTranscript((t) => (t ? { ...t, hasMore: false, awaitingReplyTo: null, state: t.state === "active" ? "waiting" : t.state } : t));
          })
          .finally(() => current && setTick((n) => n + 1));
      },
      hasMore ? 0 : pollMs,
    );
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [transport, sessionId, polling, tick, pollMs]);

  // The session's changes, read again whenever a change event (or the session's count) grows, or
  // Retry after a refused read. Each read is merged by id into what is held (mergeChanges): a card
  // is never dropped, and an answer that left before an approve returned never undoes it.
  // Held with the session they belong to, so another session's cards are never shown, not even
  // for the render before a reset would land.
  const [changeState, setChangeState] = useState<{ sessionId: string; list: AgentChange[]; error: ApiError | null }>({ sessionId, list: [], error: null });
  const [changesRound, setChangesRound] = useState(0);
  const mine = changeState.sessionId === sessionId;
  const changes = mine ? changeState.list : [];
  const changesError = mine ? changeState.error : null;
  /** Merge `incoming` into this session's changes (another session's are dropped first). */
  const mergeIn = (forSession: string, incoming: readonly AgentChange[]) =>
    setChangeState((h) => ({ sessionId: forSession, list: mergeChanges(h.sessionId === forSession ? h.list : [], incoming), error: null }));
  const wanted = transcript && session ? changesToFetch(transcript.messages, session) : 0;
  useEffect(() => {
    if (wanted === 0) return;
    let current = true;
    transport
      .changes(sessionId)
      .then((list) => current && mergeIn(sessionId, [...list].reverse()))
      .catch(
        (err: unknown) =>
          current && setChangeState((h) => ({ sessionId, list: h.sessionId === sessionId ? h.list : [], error: unexpected(err) })),
      );
    return () => {
      current = false;
    };
  }, [transport, sessionId, wanted, changesRound]);

  /** A card's change as the API answered it; a decision is followed by one poll for the reply. */
  const changed = (next: AgentChange) => {
    setChangeState((h) => (h.sessionId === sessionId ? { ...h, list: mergeChanges(h.list, [next]) } : h));
    if (next.status !== "proposed") setTranscript((t) => (t ? { ...t, hasMore: true } : t));
  };

  // A poll that finds the session ended: read it again for why it ended.
  const endedByPoll = transcript !== null && isEnded(transcript.state) && session !== null && !isEnded(session.state);
  useEffect(() => {
    if (!endedByPoll) return;
    let current = true;
    transport
      .get(sessionId)
      .then((s) => current && setSession(s))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [transport, sessionId, endedByPoll]);

  /** The rows after `before`, as the API holds them, and the session's counts. */
  const resync = (before: number) => {
    void transport
      .poll(sessionId, before)
      .then((list) => setTranscript((t) => (t ? resynced(t, before, list) : t)))
      .catch((err: unknown) => setActionError(unexpected(err)));
    void transport
      .get(sessionId)
      .then(setSession)
      .catch(() => undefined);
  };

  /** API-586: the operator's row at once, each event as it arrives, the API's rows at the end. */
  const streamTurn = (turn: NonNullable<typeof transport.turn>, text: string): Promise<boolean> => {
    const before = held.current?.lastSeq ?? 0;
    setBusy(true);
    setStreaming(true);
    setToolLimit(false);
    setActionError(null);
    // The operator's row has no seq until the history is read again: it sorts after `before`.
    setTranscript((t) => (t ? withRows(t, [streamedRow(before + 0.5, "operator", { text })]) : t));
    let started = false;
    const onEvent = (event: TurnEvent) => {
      started = true;
      if (event.event === "change") mergeIn(sessionId, [event.change]);
      const row = eventRow(event);
      if (row) setTranscript((t) => (t ? withRows(t, [row]) : t));
    };
    return turn(sessionId, text, onEvent)
      .then((end) => {
        if (end.event === "error") setActionError(new ApiError(0, end.code, end.message));
        else if (end.stopReason === "tool_limit") setToolLimit(true);
        return true;
      })
      .catch((err: unknown) => {
        setActionError(unexpected(err));
        return started;
      })
      .finally(() => {
        setStreaming(false);
        setBusy(false);
        resync(before);
      });
  };

  const send = (text: string): Promise<boolean> => {
    if (transport.turn) return streamTurn(transport.turn.bind(transport), text);
    setBusy(true);
    setActionError(null);
    return transport
      .send(sessionId, text)
      .then((message) => {
        setTranscript((t) => (t ? applySent({ ...t, state: t.state === "awaiting_operator" ? "waiting" : t.state }, message) : t));
        return true;
      })
      .catch((err: unknown) => {
        const error = unexpected(err);
        setActionError(error);
        if (error.code === "session_terminal") void transport.get(sessionId).then(setSession).catch(() => undefined);
        return false;
      })
      .finally(() => setBusy(false));
  };

  const end = () => {
    setBusy(true);
    setActionError(null);
    transport
      .end(sessionId)
      .then((ended) => {
        setSession(ended);
        setTranscript((t) => (t ? { ...t, state: ended.state, awaitingReplyTo: null } : t));
      })
      .catch((err: unknown) => setActionError(unexpected(err)))
      .finally(() => setBusy(false));
  };

  if (loadError) {
    return (
      <section class="assistant" aria-label="Assistant conversation">
        <PageHeader title="Conversation" />
        <AssistantError error={loadError} onRetry={() => setAttempt((n) => n + 1)} />
        <BackLink href={sessionsHref} />
      </section>
    );
  }
  if (!session || !transcript || !view) return <Loading label="Opening the conversation…" />;

  const state = session.state === "ended" ? "ended" : view.state;
  const ended = isEnded(state);
  const thinking = streaming || isThinking({ ...view, state });
  const ownSurface = continuesHere(host, session);
  const rows = transcriptRows(transcript.messages);

  return (
    <section class="assistant assistant-conversation" aria-label="Assistant conversation">
      <PageHeader
        title="Conversation"
        titleExtra={
          <>
            <Chip tone={STATE_TONES[state]}>{STATE_LABELS[state]}</Chip>
            <ChannelChip session={session} />
          </>
        }
        meta={<span>Started {when(session.createdAt)} by {startedBy(host, session)}</span>}
        actions={<BackLink href={sessionsHref} />}
      />
      {actionError && <TurnError error={actionError} quotasHref={onProxy(host) ? `${host.basePath}/quotas` : null} />}
      {toolLimit && !actionError && <TurnError error={new ApiError(0, "turn_tool_limit", "")} quotasHref={null} />}
      {state === "awaiting_operator" && (
        <p class="banner banner-info" role="status">
          The Assistant paused because it had not heard from you for a while. Send a message to continue.
        </p>
      )}
      {ended && (
        <p class="banner banner-info" role="status">
          {endReasonText(session.endReason)}
        </p>
      )}
      <div class="assistant-layout">
        <div class="assistant-main">
          <Transcript rows={rows} thinking={thinking} />
          {changes.length > 0 && (
            <section class="assistant-changes" aria-label="Proposed changes" aria-live="polite" aria-relevant="additions">
              {changes.map((change) => (
                <ChangeCard key={change.id} sessionId={sessionId} change={change} onChanged={changed} ended={ended} />
              ))}
            </section>
          )}
          {changesError && (
            <div class="assistant-changes-error">
              <p class="muted">The proposed changes could not be read.</p>
              <AssistantError error={changesError} onRetry={() => setChangesRound((n) => n + 1)} />
            </div>
          )}
          {!ended && ownSurface && (drivenFromClaudeCode(session) ? <ClaudeCodeNote /> : <Composer key={sessionId} initialText={initialText} disabled={busy || thinking} empty={rows.length === 0} onSend={send} />)}
          {!ended && !ownSurface && <OtherSurface proxy={onProxy(host)} />}
        </div>
        <Rail session={session} spend={!onProxy(host)} pending={changes.filter((c) => !c.withheld && effectiveStatus(c, Date.now(), ended) === "proposed").length} ended={ended} busy={busy} onEnd={end} />
      </div>
    </section>
  );
}

/** A turn's refusal in words with its Help topic (quota_exceeded links to Quotas on the proxy); any other error as the API's refusals are. */
function TurnError({ error, quotasHref }: { error: ApiError; quotasHref: string | null }) {
  const refusal = turnRefusalFrom(error);
  if (!refusal) return <AssistantError error={error} />;
  return (
    <div class="banner banner-warning assistant-refusal" role="alert" data-refusal={refusal.code}>
      <p>
        <strong>{refusal.title}</strong> {refusal.text}
        {refusal.code === "quota_exceeded" && quotasHref !== null && (
          <>
            {" "}
            <Link href={quotasHref}>See Quotas</Link>
          </>
        )}
      </p>
      <HelpButton topic={refusal.help} label={refusal.title} />
    </div>
  );
}

/** Why a session of the other surface has no composer: the API refuses every message sent to it from here (N5). */
function OtherSurface({ proxy }: { proxy: boolean }) {
  return (
    <p class="card muted assistant-other-surface" role="note">
      {proxy
        ? "AllCode support started this conversation. You can read it here, but only AllCode support can continue it."
        : "The customer's admin started this conversation on their proxy. You can read it here; it continues only on their proxy."}
    </p>
  );
}

/** Why a Claude Code session has no composer, and that its changes are still decided here. */
function ClaudeCodeNote() {
  return (
    <p class="card muted assistant-claude-code" role="note">
      This conversation is driven from Claude Code. Approve or reject its proposed changes here.{" "}
      <HelpButton topic={CLAUDE_CODE_HELP} label="a Claude Code conversation" />
    </p>
  );
}

function BackLink({ href }: { href: string }) {
  return (
    <Link href={href} class="button button-secondary">
      All conversations
    </Link>
  );
}

function Transcript({ rows, thinking }: { rows: readonly TranscriptRow[]; thinking: boolean }) {
  return (
    <div class="card assistant-transcript">
      <ol class="assistant-rows" aria-label="Transcript" aria-live="polite" aria-relevant="additions">
        {rows.map((row) => (
          <TranscriptLine key={row.seq} row={row} />
        ))}
      </ol>
      {rows.length === 0 && !thinking && <p class="muted">Nothing said yet.</p>}
      {thinking && (
        <p class="assistant-thinking" role="status">
          The Assistant is working…
        </p>
      )}
    </div>
  );
}

function TranscriptLine({ row }: { row: TranscriptRow }) {
  switch (row.kind) {
    case "operator":
    case "assistant":
      return (
        <li class={`assistant-row assistant-row-${row.kind}`}>
          <span class="assistant-row-who">{row.kind === "operator" ? "You" : "Assistant"}</span>
          <p class="assistant-row-text">{row.text}</p>
        </li>
      );
    case "tool":
      return <li class={`assistant-row assistant-row-tool${row.failed ? " assistant-row-failed" : ""}`}>{row.text}</li>;
    case "event":
      return <li class="assistant-row assistant-row-event">{row.text}</li>;
    case "hidden":
      return <li class="assistant-row assistant-row-hidden">{row.text}</li>;
  }
}

function Composer({
  initialText,
  disabled,
  empty,
  onSend,
}: {
  initialText: string;
  disabled: boolean;
  empty: boolean;
  onSend(text: string): Promise<boolean>;
}) {
  const me = useScopeMe();
  const [text, setText] = useState(initialText.slice(0, MAX_MESSAGE_LENGTH));
  const trimmed = text.trim();

  const submit = (value: string) => {
    if (disabled || value.trim() === "") return;
    void onSend(value.trim()).then((sent) => {
      if (sent) setText("");
    });
  };

  const onKeyDown = (event: JSX.TargetedKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      submit(text);
    }
  };

  return (
    <form
      class="card assistant-composer"
      aria-label="Message the Assistant"
      onSubmit={(event) => {
        event.preventDefault();
        submit(text);
      }}
    >
      {empty && (
        <ul class="assistant-starters" aria-label="Starter requests">
          {startersFor(me).map((starter) => (
            <li key={starter.tool}>
              <button type="button" class="button button-secondary button-small assistant-starter" disabled={disabled} onClick={() => submit(starter.text)}>
                {starter.text}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* help-exempt: the composer's purpose is its form's name; the label is for screen readers. */}
      <label for="assistant-message" class="visually-hidden">
        Your message
      </label>
      <textarea
        id="assistant-message"
        class="input assistant-input"
        rows={3}
        maxLength={MAX_MESSAGE_LENGTH}
        placeholder="Ask the Assistant… (Enter sends, Shift+Enter adds a line)"
        value={text}
        disabled={disabled}
        onInput={(event) => setText(event.currentTarget.value)}
        onKeyDown={onKeyDown}
      />
      <div class="assistant-composer-actions">
        <button type="submit" class="button button-primary" disabled={disabled || trimmed === ""}>
          Send
        </button>
      </div>
    </form>
  );
}

function Rail({ session, spend, pending, ended, busy, onEnd }: { session: AgentSession; spend: boolean; pending: number; ended: boolean; busy: boolean; onEnd(): void }) {
  return (
    <aside class="card assistant-rail" aria-label="Conversation details">
      <dl class="assistant-facts">
        <dt>Installation</dt>
        <dd>{session.installationName ?? "None in context"}</dd>
        <dt>Turns</dt>
        <dd>{session.turnsUsed}</dd>
        {spend && session.spentMicroUsd !== null && (
          <>
            <dt>Model spend</dt>
            <dd>
              <Money micro={session.spentMicroUsd} />
            </dd>
          </>
        )}
        {pending > 0 && (
          <>
            <dt>Waiting for approval</dt>
            <dd>{pending === 1 ? "1 change" : `${pending} changes`}</dd>
          </>
        )}
        <dt>Last activity</dt>
        <dd>{when(session.lastActivityAt)}</dd>
      </dl>
      {!ended && (
        <button type="button" class="button button-danger" disabled={busy} onClick={onEnd}>
          End conversation
        </button>
      )}
    </aside>
  );
}
